import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  ReservationDocumentRequirementStatus,
  ReservationDocumentsReturn,
  ReservationReturn,
} from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import { Button } from '../Components/Catalyst/button';
import { Field, Label } from '../Components/Catalyst/fieldset';
import { Subheading } from '../Components/Catalyst/heading';
import { Input } from '../Components/Catalyst/input';
import { Text } from '../Components/Catalyst/text';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { useImpersonating } from '../Hooks/useImpersonating';
import { loadMemberFileUrl } from '../Violations/comments/MemberAttachmentThumb';
import { todayIn } from './reservationFormat';
import { DOCUMENT_ACCEPT, DocumentTone, documentState } from './reservationPhase3';
import { reservationErrorMessage } from './ruleMessages';

interface Props {
  reservation: ReservationReturn;
}

const TONE: Record<DocumentTone, string> = {
  green: 'text-green-800 dark:text-green-300',
  amber: 'text-amber-800 dark:text-amber-300',
  red: 'text-red-800 dark:text-red-300',
  zinc: 'text-zinc-600 dark:text-zinc-400',
};

/**
 * The documents a booking needs on file (#640 phase 3) — a mover's
 * certificate of insurance, a signed waiver — and the household's uploads
 * against each. A booking that needs them stays pending until management
 * accepts every one; a rejection comes with a note saying why.
 */
const ReservationDocumentsSection: React.FC<Props> = ({ reservation }) => {
  const [docs, setDocs] = useState<ReservationDocumentsReturn | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    memberReservationService
      .listMyReservationDocuments({ reservationId: reservation.id })
      .then(({ data }) => {
        if (!cancelled) setDocs(data);
      })
      .catch((err) => {
        // A booking without requirements has nothing to show; a 404 here
        // is not worth a red box on the page.
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (!cancelled && status !== 404) {
          setError(reservationErrorMessage(err, "Couldn't load the documents for this booking."));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reservation.id]);

  const requirements = (docs?.requirements ?? []).filter((r) => r.requirement.is_active);
  if (!error && requirements.length === 0) return null;
  const active = reservation.status === 'pending' || reservation.status === 'approved';
  const today = todayIn(reservation.timezone);

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-gray-800">
      <Subheading>Documents</Subheading>
      <Text className="mt-1 text-sm">
        {reservation.status === 'pending'
          ? 'Your request is approved once your management company accepts these.'
          : 'What this booking needed on file.'}
      </Text>
      <ErrorAlert className="mt-3">{error}</ErrorAlert>
      <ul className="mt-4 space-y-5">
        {requirements.map((s) => (
          <RequirementRow
            key={s.requirement.id}
            reservationId={reservation.id}
            status={s}
            today={today}
            canUpload={active && !s.satisfied}
            onUploaded={setDocs}
          />
        ))}
      </ul>
    </section>
  );
};

interface RowProps {
  reservationId: string;
  status: ReservationDocumentRequirementStatus;
  today: string;
  canUpload: boolean;
  onUploaded: (docs: ReservationDocumentsReturn) => void;
}

const RequirementRow: React.FC<RowProps> = ({ reservationId, status, today, canUpload, onUploaded }) => {
  const impersonating = useImpersonating();
  const { requirement, latest } = status;
  const state = documentState(status, today);
  const [file, setFile] = useState<File | null>(null);
  const [expires, setExpires] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);

  const upload = async (): Promise<void> => {
    if (!file) return;
    if (requirement.requires_expiry && !expires) {
      setError('Please enter the date this document expires.');
      return;
    }
    setWorking(true);
    setError(null);
    try {
      const { data } = await memberReservationService.uploadMyReservationDocument({
        reservationId,
        requirementId: requirement.id,
        file,
        ...(expires ? { expiresOn: expires } : {}),
      });
      setFile(null);
      setExpires('');
      setInputKey((k) => k + 1);
      onUploaded(data);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't upload that file."));
    } finally {
      setWorking(false);
    }
  };

  const view = async (): Promise<void> => {
    if (!latest) return;
    try {
      const url = await loadMemberFileUrl(latest.file_id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't open that file."));
    }
  };

  return (
    <li>
      <div className="font-medium text-zinc-950 dark:text-white">{requirement.title}</div>
      {requirement.description && (
        <Text className="mt-0.5 text-sm">{requirement.description}</Text>
      )}
      <p className={clsx('mt-1 text-sm', TONE[state.tone])}>{state.text}</p>
      {latest && latest.renderable && (
        <button
          type="button"
          onClick={view}
          className="mt-1 text-sm text-sky-700 hover:underline dark:text-sky-400"
        >
          View {latest.file_name}
        </button>
      )}
      {canUpload && (
        <div className="mt-3 space-y-3">
          <Field>
            <Label>{latest ? 'Upload a new file' : 'Upload'}</Label>
            <input
              key={inputKey}
              type="file"
              accept={DOCUMENT_ACCEPT}
              disabled={impersonating || working}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-2 block w-full text-sm text-zinc-700 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-100 file:px-3 file:py-2 file:text-sm file:font-medium dark:text-zinc-300 dark:file:bg-zinc-700 dark:file:text-white"
            />
          </Field>
          {requirement.requires_expiry && (
            <Field>
              <Label>Expires on</Label>
              <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
            </Field>
          )}
          <ErrorAlert>{error}</ErrorAlert>
          <Button outline onClick={upload} disabled={!file || working || impersonating}>
            {working ? 'Uploading…' : 'Send'}
          </Button>
          {impersonating && (
            <Text className="text-sm">This is a read-only view, so files can't be uploaded here.</Text>
          )}
        </div>
      )}
      {!canUpload && error && <ErrorAlert className="mt-2">{error}</ErrorAlert>}
    </li>
  );
};

export default ReservationDocumentsSection;
